import { useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  ContactProjectsListDataTableDocument,
  useContactProjectsListDataTable,
} from 'generated/resources/datatable.graphql'
import {
  BulkContactAssignmentProjectDocument,
  BulkContactAssignmentProjectMutation,
  ContactFragment,
  ContactsDetailsPageRouteDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import { ContactProjectListPermissions } from 'modules/finance/hooks/useContactProjectListPermissions'

import ContactsAddProjectsModal from './ContactsAddProjectsModal'
import { CreateWizard } from './CreateWizard'
import { BillableFilter } from './Filters/BillableFilter'
import { LegalEntityFilter } from './Filters/LegalEntityFilter'
import { StatusFilter } from './Filters/StatusFilter'
import NoProjectsMessage from './NoProjectsMessage'
import { clientProjectsListProjections } from './projections'

type Props = {
  contact: ContactFragment
  policies: ContactProjectListPermissions['policies']
  hasProjectCosts: boolean
}

const ContactsProjectsList = ({ contact, policies, hasProjectCosts }: Props) => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()
  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const [showAddProjects, setShowAddProjects] = useState(false)
  const [showLinkProjects, setShowLinkProjects] = useState(false)

  const [manage] = useMutation(BulkContactAssignmentProjectDocument, {
    extractorFn: (data: BulkContactAssignmentProjectMutation) =>
      data.projectManagementMutations.bulkClientAssignmentProjectmanagementProject,
  })

  const removeProject = async (projectId: number) => {
    await manage({
      variables: {
        companyId: company.id,
        projectIds: [projectId],
      },
      awaitRefetchQueries: true,
      refetchQueries: [ContactProjectsListDataTableDocument, ContactsDetailsPageRouteDocument],
    })
  }

  const source = useContactProjectsListDataTable(
    clientProjectsListProjections({
      locale,
      hasCost: hasProjectCosts,
      removeProject,
      hasBillable: policies.hasCostsFeature,
      hasFixedTimeBudget: policies.hasFixedTimeBudget,
      canManageProjects: policies.canManageProjects,
    }),
    {
      variables: {
        clientId: contact.id,
        search: debouncedSearch,
        hasBillable: policies.hasCostsFeature,
        hasCost: policies.canSeeCosts,
        hasSpendings: policies.canSeeCosts && policies.hasExpenses,
        hasFixedTimeBudget: policies.hasFixedTimeBudget,
      },
    }
  )

  const filters = compact([
    StatusFilter(),
    policies.hasCostsFeature ? BillableFilter() : undefined,
    LegalEntityFilter(),
  ])

  return (
    <Box paddingTop="s24">
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 12,
          infiniteScroll: true,
        }}
        sort={{
          path: 'status',
        }}
        filters={filters}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableExport
            customPopoverOptions={
              <>
                <PopoverMenu.TouchableOption
                  title={`${i18n.t(
                    'finance.contacts.contact_details.projects.no_projects_message.options.new'
                  )}`}
                  onClick={() => setShowAddProjects(true)}
                />
                <PopoverMenu.TouchableOption
                  title={`${i18n.t(
                    'finance.contacts.contact_details.projects.no_projects_message.options.link'
                  )}`}
                  onClick={() => setShowLinkProjects(true)}
                />
              </>
            }
            search={{
              placeholder: i18n.t('common.search_by'),
              value: search,
              onChange: onSearch,
            }}
          />
          <DataTable.Table
            emptyState={
              <NoProjectsMessage
                onClickAddButton={() => setShowAddProjects(true)}
                onClickLinkButton={() => setShowLinkProjects(true)}
              />
            }
            autoWidth
          />
        </Box>
      </DataTable.Provider>
      {showLinkProjects && (
        <ContactsAddProjectsModal
          onClose={() => setShowLinkProjects(false)}
          clientId={contact.id}
        />
      )}
      {showAddProjects && (
        <CreateWizard
          client={contact}
          onClose={() => {
            setShowAddProjects(false)
          }}
          onSuccess={() => {
            setShowAddProjects(false)
          }}
        />
      )}
    </Box>
  )
}

export default ContactsProjectsList
